import { useState, useEffect } from "react";
import { usePortailAuth } from "../lib/auth-context";
import { usePortailRouter } from "../lib/router-context";
import { authFetch } from "../lib/authFetch";
import { ArrowLeft, ArrowRight, Check, Loader2, Upload, X, AlertTriangle } from "lucide-react";

const TYPES_PRESTATION = [
  { value: "installation", label: "🤖 Installation robot", desc: "Mise en place et configuration sur site" },
  { value: "maintenance", label: "🔧 Maintenance / Réparation", desc: "Maintenance préventive ou curative" },
  { value: "formation", label: "📚 Formation", desc: "Formation des équipes" },
  { value: "demonstration", label: "🎯 Démonstration", desc: "Présentation commerciale" },
  { value: "depannage", label: "⚡ Dépannage urgent", desc: "Intervention urgente / panne" },
  { value: "autre", label: "📋 Autre prestation", desc: "Autre type d'intervention" },
];

const MODELES_ROBOT = ["BellaBot", "KettyBot", "PuduBot", "HolaBot", "KettyBot Pro", "BellaBot Pro", "FlashBot", "Autre"];
const PIECES_SUSPECTES_LIST = ["Batterie", "Roue motrice", "Capteur LiDAR", "Écran tactile", "Carte mère", "Motoréducteur", "Capteur ultrason", "Caméra", "Câblage interne", "Autre"];
const URGENCES = [
  { value: "faible", label: "🟢 Faible", desc: "Flexible" },
  { value: "normal", label: "🔵 Normal", desc: "Prochains jours" },
  { value: "urgent", label: "🟠 Urgent", desc: "< 48h" },
  { value: "critique", label: "🔴 Critique", desc: "Immédiat" },
];

interface FormData {
  typePrestation: string;
  niveauUrgence: string;
  siteNom: string; siteAdresse: string;
  clientFinalNom: string; clientFinalAdresse: string;
  contactTerrainNom: string; contactTerrainTel: string; contactTerrainEmail: string;
  modeleRobot: string; numeroSerie: string;
  descriptionPanne: string; piecesSuspectes: string[];
  dateIntervention: string; dateInterventionFin: string;
  titre: string; description: string;
  files: File[];
}

const EMPTY: FormData = {
  typePrestation: "", niveauUrgence: "normal",
  siteNom: "", siteAdresse: "", clientFinalNom: "", clientFinalAdresse: "",
  contactTerrainNom: "", contactTerrainTel: "", contactTerrainEmail: "",
  modeleRobot: "", numeroSerie: "", descriptionPanne: "", piecesSuspectes: [],
  dateIntervention: "", dateInterventionFin: "", titre: "", description: "",
  files: [],
};

// 8 étapes requises
const STEPS = [
  { num: 1, label: "Type" },
  { num: 2, label: "Urgence" },
  { num: 3, label: "Site" },
  { num: 4, label: "Contact" },
  { num: 5, label: "Robot" },
  { num: 6, label: "Panne" },
  { num: 7, label: "Pièces jointes" },
  { num: 8, label: "Récapitulatif" },
];

export default function NouvelleDemandeWizardPage() {
  const { brand } = usePortailAuth();
  const { navigate } = usePortailRouter();
  const primary = brand?.couleurPrimaire || "#2563eb";

  const [step, setStep] = useState(1);
  const [form, setForm] = useState<FormData>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [sites, setSites] = useState<Array<{ site_nom: string; site_adresse: string }>>([]);

  useEffect(() => {
    authFetch("/api/portail/prestations/sites-equipes").then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.sites) setSites(data.sites); })
      .catch(() => {});
  }, []);

  function set<K extends keyof FormData>(k: K, v: FormData[K]) {
    setForm(f => ({ ...f, [k]: v }));
  }

  function togglePiece(p: string) {
    set("piecesSuspectes", form.piecesSuspectes.includes(p)
      ? form.piecesSuspectes.filter(x => x !== p)
      : [...form.piecesSuspectes, p]);
  }

  function validateStep(s: number): string | null {
    switch (s) {
      case 1: return form.typePrestation ? null : "Sélectionnez un type de prestation.";
      case 2: return form.niveauUrgence ? null : "Sélectionnez un niveau d'urgence.";
      case 3: return null; // site optionnel
      case 4: {
        if (form.contactTerrainEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contactTerrainEmail)) return "Email de contact invalide.";
        if (form.contactTerrainTel && form.contactTerrainTel.replace(/\D/g, "").length < 8) return "Numéro de téléphone invalide.";
        return null;
      }
      case 5: {
        if (form.dateIntervention && form.dateInterventionFin && form.dateInterventionFin < form.dateIntervention) return "La date de fin ne peut pas être antérieure à la date de début.";
        return null;
      }
      case 6: return null; // description panne optionnelle
      case 7: return null; // pièces jointes optionnelles
      case 8: return form.titre.trim() ? null : "Le titre est requis.";
      default: return null;
    }
  }

  function next() {
    setError(null);
    const err = validateStep(step);
    if (err) { setError(err); return; }
    if (step < STEPS.length) setStep(s => s + 1);
  }

  function prev() {
    setError(null);
    if (step > 1) setStep(s => s - 1);
    else navigate("/prestations");
  }

  async function handleSubmit() {
    if (!form.titre.trim()) { setError("Le titre est requis."); return; }
    setSubmitting(true); setError(null);
    try {
      const fd = new FormData();
      fd.append("typePrestation", form.typePrestation);
      fd.append("niveauUrgence", form.niveauUrgence);
      fd.append("titre", form.titre.trim());
      if (form.description) fd.append("description", form.description);
      if (form.siteNom) fd.append("siteNom", form.siteNom);
      if (form.siteAdresse) fd.append("siteAdresse", form.siteAdresse);
      if (form.clientFinalNom) fd.append("clientFinalNom", form.clientFinalNom);
      if (form.clientFinalAdresse) fd.append("clientFinalAdresse", form.clientFinalAdresse);
      if (form.contactTerrainNom) fd.append("contactTerrainNom", form.contactTerrainNom);
      if (form.contactTerrainTel) fd.append("contactTerrainTel", form.contactTerrainTel);
      if (form.contactTerrainEmail) fd.append("contactTerrainEmail", form.contactTerrainEmail);
      if (form.modeleRobot) fd.append("modeleRobot", form.modeleRobot);
      if (form.numeroSerie) fd.append("numeroSerie", form.numeroSerie);
      if (form.descriptionPanne) fd.append("descriptionPanne", form.descriptionPanne);
      if (form.piecesSuspectes.length) fd.append("piecesSuspectes", JSON.stringify(form.piecesSuspectes));
      if (form.dateIntervention) fd.append("dateIntervention", form.dateIntervention);
      if (form.dateInterventionFin) fd.append("dateInterventionFin", form.dateInterventionFin);
      form.files.forEach(f => fd.append("pj", f));

      const res = await authFetch("/api/portail/prestations", { method: "POST", body: fd });
      if (!res.ok) { const err = await res.json(); setError(err.message || "Erreur."); return; }
      const data = await res.json();
      setDone(data.numero);
    } catch { setError("Erreur réseau."); }
    finally { setSubmitting(false); }
  }

  if (done) return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="text-center space-y-6 py-16">
        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto" style={{ backgroundColor: `${primary}20` }}>
          <Check className="w-8 h-8" style={{ color: primary }} />
        </div>
        <div>
          <h2 className="text-xl font-bold text-foreground">Demande envoyée !</h2>
          <p className="text-muted-foreground mt-2">Votre demande <span className="font-mono font-semibold">{done}</span> a été transmise à l'équipe Jobotto. Un accusé de réception vous a été envoyé par email. Vous recevrez un devis prochainement.</p>
        </div>
        <button onClick={() => navigate("/prestations")} className="px-6 py-2 rounded-lg text-sm font-medium text-white hover:opacity-90 transition" style={{ backgroundColor: primary }} data-testid="btn-back-list">
          Voir mes demandes
        </button>
      </div>
    </div>
  );

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={prev} className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition" data-testid="btn-back">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-foreground">Nouvelle demande</h1>
          <p className="text-xs text-muted-foreground mt-0.5">Étape {step} sur {STEPS.length} — {STEPS[step - 1].label}</p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="flex gap-1">
        {STEPS.map(s => (
          <div key={s.num} className="flex-1 h-1.5 rounded-full transition-all" style={{ backgroundColor: step >= s.num ? primary : "#e5e7eb" }} />
        ))}
      </div>

      {error && (
        <div className="flex items-center gap-2 text-destructive text-sm p-3 bg-destructive/10 rounded-lg">
          <AlertTriangle className="w-4 h-4 shrink-0" /> {error}
        </div>
      )}

      {/* ── STEP 1 : Type de prestation ── */}
      {step === 1 && (
        <div className="space-y-4">
          <p className="text-sm font-semibold text-foreground">Quel type de prestation souhaitez-vous demander ? *</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {TYPES_PRESTATION.map(t => (
              <button key={t.value} onClick={() => set("typePrestation", t.value)}
                className={`text-left p-3.5 rounded-xl border-2 transition-all ${form.typePrestation === t.value ? "border-transparent" : "border-border/50 hover:border-border"}`}
                style={form.typePrestation === t.value ? { borderColor: primary, backgroundColor: `${primary}10` } : {}}
                data-testid={`type-${t.value}`}>
                <p className="font-medium text-sm">{t.label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t.desc}</p>
              </button>
            ))}
          </div>
          <div className="flex justify-end">
            <button onClick={next} className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium text-white hover:opacity-90 transition" style={{ backgroundColor: primary }} data-testid="btn-next">
              Suivant <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 2 : Urgence ── */}
      {step === 2 && (
        <div className="space-y-4">
          <p className="text-sm font-semibold text-foreground">Quel est le niveau d'urgence ?</p>
          <div className="grid grid-cols-2 gap-3">
            {URGENCES.map(u => (
              <button key={u.value} onClick={() => set("niveauUrgence", u.value)}
                className={`p-4 rounded-xl border-2 text-left transition-all ${form.niveauUrgence === u.value ? "border-transparent" : "border-border/50 hover:border-border"}`}
                style={form.niveauUrgence === u.value ? { borderColor: primary, backgroundColor: `${primary}10` } : {}}
                data-testid={`urgence-${u.value}`}>
                <p className="text-sm font-medium">{u.label}</p>
                <p className="text-xs text-muted-foreground mt-1">{u.desc}</p>
              </button>
            ))}
          </div>
          {form.niveauUrgence === "critique" && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">
              ⚠️ Pour une urgence critique, n'hésitez pas à appeler directement notre service technique.
            </div>
          )}
          <div className="flex items-center justify-between">
            <button onClick={prev} className="text-sm text-muted-foreground hover:text-foreground">← Retour</button>
            <button onClick={next} className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium text-white hover:opacity-90 transition" style={{ backgroundColor: primary }} data-testid="btn-next">
              Suivant <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 3 : Site / Client ── */}
      {step === 3 && (
        <div className="space-y-4">
          <p className="text-sm font-semibold text-foreground">Lieu d'intervention</p>
          {sites.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs text-muted-foreground">Sites enregistrés</p>
              <div className="max-h-40 overflow-y-auto space-y-1.5 pr-1">
                {sites.map(s => (
                  <button key={s.site_nom} onClick={() => { set("siteNom", s.site_nom); set("siteAdresse", s.site_adresse || ""); }}
                    className={`w-full text-left p-2.5 rounded-lg border text-sm transition-all ${form.siteNom === s.site_nom ? "border-transparent" : "border-border/50 hover:border-border"}`}
                    style={form.siteNom === s.site_nom ? { borderColor: primary, backgroundColor: `${primary}08` } : {}}>
                    <p className="font-medium">{s.site_nom}</p>
                    {s.site_adresse && <p className="text-xs text-muted-foreground">{s.site_adresse}</p>}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-sm text-muted-foreground">Nom du site</label>
              <input className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="Hôtel du Parc…" value={form.siteNom} onChange={e => set("siteNom", e.target.value)} data-testid="input-site-nom" />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm text-muted-foreground">Adresse</label>
              <input className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="123 rue, 75001 Paris" value={form.siteAdresse} onChange={e => set("siteAdresse", e.target.value)} data-testid="input-site-adresse" />
            </div>
          </div>
          <div className="border-t border-border/40 pt-3 space-y-2">
            <p className="text-xs text-muted-foreground">Client final (si différent du site)</p>
            <div className="grid grid-cols-2 gap-3">
              <input className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="Nom client" value={form.clientFinalNom} onChange={e => set("clientFinalNom", e.target.value)} data-testid="input-client" />
              <input className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="Adresse client" value={form.clientFinalAdresse} onChange={e => set("clientFinalAdresse", e.target.value)} />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <button onClick={prev} className="text-sm text-muted-foreground hover:text-foreground">← Retour</button>
            <button onClick={next} className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium text-white hover:opacity-90 transition" style={{ backgroundColor: primary }} data-testid="btn-next">
              Suivant <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 4 : Contact terrain ── */}
      {step === 4 && (
        <div className="space-y-4">
          <div>
            <p className="text-sm font-semibold text-foreground">Contact terrain sur site</p>
            <p className="text-xs text-muted-foreground mt-0.5">Personne joignable le jour de l'intervention</p>
          </div>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <label className="text-sm text-muted-foreground">Nom et prénom</label>
              <input className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="Jean Dupont" value={form.contactTerrainNom} onChange={e => set("contactTerrainNom", e.target.value)} data-testid="input-contact-nom" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-sm text-muted-foreground">Téléphone</label>
                <input type="tel" className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="+33 6 …" value={form.contactTerrainTel} onChange={e => set("contactTerrainTel", e.target.value)} data-testid="input-contact-tel" />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm text-muted-foreground">Email</label>
                <input type="email" className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="contact@site.fr" value={form.contactTerrainEmail} onChange={e => set("contactTerrainEmail", e.target.value)} data-testid="input-contact-email" />
              </div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground italic">Ces coordonnées sont optionnelles mais facilitent l'intervention.</p>
          <div className="flex items-center justify-between">
            <button onClick={prev} className="text-sm text-muted-foreground hover:text-foreground">← Retour</button>
            <button onClick={next} className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium text-white hover:opacity-90 transition" style={{ backgroundColor: primary }} data-testid="btn-next">
              Suivant <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 5 : Robot concerné ── */}
      {step === 5 && (
        <div className="space-y-4">
          <p className="text-sm font-semibold text-foreground">Robot concerné</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-sm text-muted-foreground">Modèle</label>
              <select className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" value={form.modeleRobot} onChange={e => set("modeleRobot", e.target.value)} data-testid="select-modele">
                <option value="">— Sélectionner —</option>
                {MODELES_ROBOT.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm text-muted-foreground">Numéro de série</label>
              <input className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="S/N" value={form.numeroSerie} onChange={e => set("numeroSerie", e.target.value)} data-testid="input-sn" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-sm text-muted-foreground">Date souhaitée (début)</label>
              <input type="date" className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" value={form.dateIntervention} onChange={e => set("dateIntervention", e.target.value)} data-testid="input-date-debut" />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm text-muted-foreground">Date souhaitée (fin)</label>
              <input type="date" className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" value={form.dateInterventionFin} onChange={e => set("dateInterventionFin", e.target.value)} />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <button onClick={prev} className="text-sm text-muted-foreground hover:text-foreground">← Retour</button>
            <button onClick={next} className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium text-white hover:opacity-90 transition" style={{ backgroundColor: primary }} data-testid="btn-next">
              Suivant <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 6 : Panne + Pièces suspectes ── */}
      {step === 6 && (
        <div className="space-y-4">
          <p className="text-sm font-semibold text-foreground">Description de la panne</p>
          <div className="space-y-1.5">
            <label className="text-sm text-muted-foreground">Symptômes observés</label>
            <textarea className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none" rows={4} placeholder="Décrivez les symptômes, le comportement observé, les conditions d'apparition de la panne…" value={form.descriptionPanne} onChange={e => set("descriptionPanne", e.target.value)} data-testid="input-panne" />
          </div>
          <div className="space-y-2">
            <label className="text-sm text-muted-foreground">Pièces potentiellement en cause (optionnel)</label>
            <div className="flex flex-wrap gap-2">
              {PIECES_SUSPECTES_LIST.map(p => (
                <button key={p} onClick={() => togglePiece(p)}
                  className={`px-3 py-1.5 rounded-lg text-xs border transition-all ${form.piecesSuspectes.includes(p) ? "text-white border-transparent" : "border-border/50 text-muted-foreground hover:border-border"}`}
                  style={form.piecesSuspectes.includes(p) ? { backgroundColor: primary, borderColor: primary } : {}}
                  data-testid={`piece-${p.replace(/\s/g, "-")}`}>
                  {p}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-between">
            <button onClick={prev} className="text-sm text-muted-foreground hover:text-foreground">← Retour</button>
            <button onClick={next} className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium text-white hover:opacity-90 transition" style={{ backgroundColor: primary }} data-testid="btn-next">
              Suivant <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 7 : Pièces jointes ── */}
      {step === 7 && (
        <div className="space-y-4">
          <div>
            <p className="text-sm font-semibold text-foreground">Pièces jointes (optionnel)</p>
            <p className="text-xs text-muted-foreground mt-0.5">Photos de la panne, vidéos, bons de commande, documents — max 5 fichiers</p>
          </div>
          <label className="flex flex-col items-center justify-center gap-2 p-8 border-2 border-dashed border-border rounded-xl cursor-pointer hover:border-primary/50 transition-colors" data-testid="drop-zone">
            <Upload className="w-6 h-6 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Cliquer pour sélectionner</span>
            <span className="text-xs text-muted-foreground">Images, vidéos, PDF, documents</span>
            <input type="file" multiple accept="image/*,video/*,application/pdf,.doc,.docx,.xlsx" className="hidden"
              onChange={e => set("files", [...form.files, ...Array.from(e.target.files || [])].slice(0, 5))} />
          </label>
          {form.files.length > 0 && (
            <div className="space-y-1.5">
              {form.files.map((f, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg text-sm">
                  <span className="truncate text-foreground flex-1 mr-2">{f.name}</span>
                  <span className="text-xs text-muted-foreground shrink-0 mr-2">{Math.round(f.size / 1024)} Ko</span>
                  <button onClick={() => set("files", form.files.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-destructive transition-colors"><X className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center justify-between">
            <button onClick={prev} className="text-sm text-muted-foreground hover:text-foreground">← Retour</button>
            <button onClick={next} className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium text-white hover:opacity-90 transition" style={{ backgroundColor: primary }} data-testid="btn-next">
              Suivant <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 8 : Titre + Récapitulatif ── */}
      {step === 8 && (
        <div className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-foreground">Titre de la demande *</label>
            <input className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="Ex : Installation BellaBot — Restaurant Le Grand" value={form.titre} onChange={e => set("titre", e.target.value)} data-testid="input-titre" />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm text-muted-foreground">Note complémentaire</label>
            <textarea className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none" rows={2} placeholder="Informations supplémentaires…" value={form.description} onChange={e => set("description", e.target.value)} />
          </div>

          {/* Récapitulatif */}
          <div className="bg-secondary/30 rounded-xl p-4 space-y-2 border border-border/40">
            <p className="text-xs font-semibold text-foreground mb-2">Récapitulatif de votre demande</p>
            <div className="grid grid-cols-[120px_1fr] gap-x-3 gap-y-1.5 text-xs">
              <span className="text-muted-foreground">Type</span>
              <span className="font-medium">{TYPES_PRESTATION.find(t => t.value === form.typePrestation)?.label || "—"}</span>
              <span className="text-muted-foreground">Urgence</span>
              <span>{URGENCES.find(u => u.value === form.niveauUrgence)?.label || "—"}</span>
              {form.siteNom && <><span className="text-muted-foreground">Site</span><span>{form.siteNom}{form.siteAdresse ? `, ${form.siteAdresse}` : ""}</span></>}
              {form.clientFinalNom && <><span className="text-muted-foreground">Client</span><span>{form.clientFinalNom}</span></>}
              {form.contactTerrainNom && <><span className="text-muted-foreground">Contact</span><span>{form.contactTerrainNom}{form.contactTerrainTel ? ` — ${form.contactTerrainTel}` : ""}</span></>}
              {form.modeleRobot && <><span className="text-muted-foreground">Robot</span><span>{form.modeleRobot}{form.numeroSerie ? ` — S/N ${form.numeroSerie}` : ""}</span></>}
              {form.dateIntervention && <><span className="text-muted-foreground">Date</span><span>{new Date(form.dateIntervention).toLocaleDateString("fr-FR")}</span></>}
              {form.descriptionPanne && <><span className="text-muted-foreground">Panne</span><span className="line-clamp-2">{form.descriptionPanne}</span></>}
              {form.piecesSuspectes.length > 0 && <><span className="text-muted-foreground">Pièces</span><span>{form.piecesSuspectes.join(", ")}</span></>}
              {form.files.length > 0 && <><span className="text-muted-foreground">Fichiers</span><span>{form.files.length} fichier(s)</span></>}
            </div>
          </div>

          <div className="flex items-center justify-between pt-1">
            <button onClick={prev} className="text-sm text-muted-foreground hover:text-foreground">← Retour</button>
            <button onClick={handleSubmit} disabled={submitting || !form.titre.trim()}
              className="flex items-center gap-2 px-6 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-60 hover:opacity-90 transition"
              style={{ backgroundColor: primary }} data-testid="btn-submit">
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Envoyer la demande
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
